import {
  ColumnsType,
  DeleteIcon,
  EditIcon,
  InfiniteTable,
  MultipleContactModal,
} from '@/components/_common';
import {
  useCreateCustomerContact,
  useCustomerContactDetailRemove,
  useCustomerContacts,
  useDeleteCustomerContact,
  useUpdateCustomerContact,
} from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { CustomerContact } from '@/core/domain/people/CustomerContacts/CustomerContacts.types';
import { useCustomerGeneral } from '@/core/domain/people/Customers/Customers.hooks';
import { useDisclosure } from '@/hooks';
import { useContactColumns } from '@/hooks/useContactColumns';
import { removeMaskDetailList } from '@/utils/mask/removeMaskDetailList';
import { Button, Collapse, Divider, Form, message, Modal, Space } from 'antd';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';

import './style.less';
import SkeletonCustom from '@/components/_common/SkeletonCustom';
import { isAppPortocar } from '@/index';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { ContactDetailList } from '@/core/domain/people/_shared/Contact';

interface Props {
  customerId?: number;
}

const CustomerContactCollapse: React.FC<Props> = ({ customerId }) => {
  const intl = useIntl();
  const { Panel } = Collapse;
  const [form] = Form.useForm();
  const { columns: columnsContact } = useContactColumns<CustomerContact>();

  const [viewContact, setViewContact] = useDisclosure();

  const customerContacts = useCustomerContacts(customerId);
  const customerGeneral = useCustomerGeneral(customerId);
  const updateContactMutation = useUpdateCustomerContact();
  const createContactMutation = useCreateCustomerContact();
  const customerContactDetailRemove = useCustomerContactDetailRemove();
  const deleteContactMutation = useDeleteCustomerContact();

  const contacts = customerContacts.data || [];

  function handleContactSelect(employeeContact: CustomerContact) {
    form.setFieldsValue({
      ...employeeContact,
      detailList: employeeContact.detailList,
      contactId: employeeContact.id,
      birthDate: employeeContact.birthDate
        ? moment(employeeContact.birthDate)
        : null,
    });
  }

  const handleRemoveClick = (contact: CustomerContact) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.contact.remove',
      }),
      content: `${intl.formatMessage({
        id: 'customers.contact.remove.alert',
      })} ${contact.name}`,
      onOk: async () => {
        if (contacts.length === 1 && isAppPortocar) {
          message.destroy();
          message.warning(
            'O cadastro requer pelo menos um item de contatos obrigatório.',
          );
        } else {
          await deleteContactMutation.mutateAsync({
            customerId: contact.customerId,
            contactId: contact.id,
          });
        }
      },
      okType: 'danger',
    });
  };

  async function handleSave() {
    try {
      const { contactId, ...values } = await form.validateFields();

      const detailList = removeMaskDetailList(values.detailList);

      if (
        isAppPortocar &&
        (![ContactType.Email].some((type) =>
          detailList.some((item) => item.contactType === type),
        ) ||
          ![ContactType.Mobile, ContactType.Phone, ContactType.WhatsApp].some(
            (type) => detailList.some((item) => item.contactType === type),
          ))
      ) {
        message.destroy();
        message.warning(
          'O cadastro requer pelo menos um e-mail e um tipo de telefone para contato.',
        );
        return;
      }

      if (contactId && !!customerId) {
        await updateContactMutation.mutateAsync({
          ...values,
          detailList,
          customerId: customerId,
          id: contactId,
        });
        setViewContact.close();
        form.resetFields();
      } else if (!!customerId) {
        await createContactMutation.mutateAsync({
          value: { ...values, detailList },
          customerId: customerId,
        });
        setViewContact.close();
        form.resetFields();
      }
    } catch {}
  }

  async function handleRemoveContactDetail(detailId: number) {
    if (isAppPortocar) {
      const formValues = form.getFieldsValue();

      const detailList =
        (formValues.detailList as ContactDetailList[]) || undefined;

      const contactType = detailList?.find(
        (detail) => detail.id === detailId,
      )?.contactType;

      const isEmail = contactType === ContactType.Email;

      const emails = detailList.filter(
        (detail) =>
          detail.id !== detailId && detail.contactType === ContactType.Email,
      );

      const phones = detailList.filter(
        (detail) =>
          detail.id !== detailId &&
          (detail.contactType === ContactType.Mobile ||
            detail.contactType === ContactType.Phone ||
            detail.contactType === ContactType.WhatsApp),
      );

      if (isEmail ? emails.length === 0 : phones.length === 0) {
        message.destroy();
        message.warning(
          `O cadastro requer pelo menos um ${
            contactType === ContactType.Email ? 'e-mail' : 'telefone'
          } obrigatório.`,
        );
        return;
      }
    }

    if (!!customerId) {
      await customerContactDetailRemove.mutateAsync({
        customerId,
        detailId,
      });
    }
  }

  const columns: ColumnsType<CustomerContact> = [
    ...columnsContact,
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      align: 'center',
      fixed: 'right',
      width: 80,
      render: (_, row: CustomerContact) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                handleContactSelect(row);
                setViewContact.open();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleRemoveClick(row)} />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (customerContacts.data?.length && viewContact) {
      const contactUpdated = customerContacts.data.find(
        (contact) => contact.id === form.getFieldValue('contactId'),
      );

      if (contactUpdated) {
        handleContactSelect(contactUpdated);
      }
    }
  }, [customerContacts.data, viewContact]);

  if (!customerId) {
    return null;
  }

  return (
    <SkeletonCustom
      loading={customerContacts.isLoading}
      skeletonType="INPUT"
      skeletonCount={1}
      style={{ marginBottom: 12 }}
    >
      <Collapse
        accordion
        className="contact-collapse"
        defaultActiveKey={!!customerContacts.data?.length ? ['1'] : undefined}
      >
        <Panel
          header="Contatos"
          extra={
            !!customerId && (
              <Button
                shape="round"
                block
                type="primary"
                onClick={(event) => {
                  if (!!customerContacts.data?.length) event.stopPropagation();

                  setViewContact.open();
                }}
              >
                Adicionar
              </Button>
            )
          }
          key="1"
        >
          {!!customerId && (
            <>
              <InfiniteTable
                dataSource={customerContacts.data}
                rowKey={(row) => row.id}
                columns={columns}
                loading={customerContacts.isFetching}
                pagination={false}
              />

              <MultipleContactModal
                open={viewContact}
                onCancel={setViewContact.close}
                onOk={handleSave}
                confirmLoading={
                  createContactMutation.isLoading ||
                  updateContactMutation.isLoading
                }
                form={form}
                personType={
                  customerGeneral.data
                    ? customerGeneral.data.personType === 'NATURAL'
                      ? 'NaturalPerson'
                      : 'LegalPerson'
                    : 'LegalPerson'
                }
                onDetailRemove={handleRemoveContactDetail}
              />
            </>
          )}
        </Panel>
      </Collapse>
    </SkeletonCustom>
  );
};

export default CustomerContactCollapse;
